import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Tooltip } from 'antd';
import { Select } from 'components/select/select';
import { ExportOutlined, FileSearchOutlined, SearchOutlined, StarFilled } from '@ant-design/icons';
import { Page } from 'components/page/page';
import { Card } from 'components/card/card';
import { Table } from 'components/table/table';
import { Badge, UserLink, CopyablePhone } from 'components';
import { Input } from 'components/input/input';
import { Button, buttonType } from 'components/button/button';
import { PaginationList, SelectedTeam, TeamSelectionUser, WorldCupTeam } from 'core/domain';
import { getTeamSelectionUsers } from 'core/repositories/world-cup-teams';
import { getViaAuth, responseValidator } from 'apis/api';
import { API } from 'apis';
import { faNumToEnNum } from 'core/utils/decimalExtentions';
import { downloadCSV } from 'core/utils/export-to-file';
import { SERVER_URL } from 'core/constants/constants';
import moment from 'jalali-moment';
import { TableColumn } from 'react-data-table-component';
import './world-cup-participants.scss';

// SortBy: 1 = LastSelectedDate, 2 = Score
// SortDirection: 1 = Asc, 2 = Desc
const DEFAULT_SORT_BY = 1;
const DEFAULT_SORT_DIR = 2;
const PAGE_SIZE = 20;

const buildQuery = (kw: string, sortBy: number, sortDirection: number): string => {
    const parts: string[] = [];
    if (kw) parts.push(`Keyword=${kw}`);
    parts.push(`SortBy=${sortBy}`);
    parts.push(`SortDirection=${sortDirection}`);
    return `?${parts.join('&')}`;
};

const SORT_BY_OPTIONS = [
    { value: 1, label: 'آخرین تاریخ انتخاب' },
    { value: 2, label: 'امتیاز' },
];

const SORT_DIR_OPTIONS = [
    { value: 2, label: 'نزولی ↓' },
    { value: 1, label: 'صعودی ↑' },
];

const flattenForExport = (items: TeamSelectionUser[]) =>
    items.map((row) => ({
        'نام و نام‌خانوادگی': row.fullName || '',
        'شماره موبایل': row.phoneNumber,
        امتیاز: row.score,
        'تعداد انتخاب': row.selectedTeamsCount,
        'آخرین تاریخ انتخاب': row.lastSelectedDate
            ? moment.utc(row.lastSelectedDate).locale('fa').local().format('YYYY/MM/DD HH:mm')
            : '',
        'تیم اول': row.teams?.find((t) => t.selectionType.id === 1)?.teamName || '',
        'تیم دوم': row.teams?.find((t) => t.selectionType.id === 2)?.teamName || '',
        'تیم سوم': row.teams?.find((t) => t.selectionType.id === 3)?.teamName || '',
    }));

export const WorldCupParticipants: React.FC = () => {
    const [data, setData] = React.useState<PaginationList<TeamSelectionUser>>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [keyword, setKeyword] = React.useState<string>('');
    const [sortBy, setSortBy] = React.useState<number>(DEFAULT_SORT_BY);
    const [sortDirection, setSortDirection] = React.useState<number>(DEFAULT_SORT_DIR);
    const [query, setQuery] = React.useState<string>(buildQuery('', DEFAULT_SORT_BY, DEFAULT_SORT_DIR));
    const [pageSize, setPageSize] = React.useState<number>(PAGE_SIZE);
    const [accessDenied, setAccessDenied] = React.useState<boolean>(false);
    const [teamsMap, setTeamsMap] = React.useState<Record<number, WorldCupTeam>>({});

    // Per-user score-logs: navigate to a dedicated page
    const navigate = useNavigate();

    const openScoreLogs = (row: TeamSelectionUser) => {
        navigate(`/dashboard/user-score-logs/${row.userId}`, {
            state: { fullName: row.fullName, phoneNumber: row.phoneNumber },
        });
    };

    // Refs let the debounce callback always read the latest sort values
    const sortByRef = React.useRef<number>(DEFAULT_SORT_BY);
    const sortDirRef = React.useRef<number>(DEFAULT_SORT_DIR);

    // ── Initial load ──────────────────────────────────────────────────────
    React.useEffect(() => {
        const q = buildQuery('', DEFAULT_SORT_BY, DEFAULT_SORT_DIR);
        getTeamSelectionUsers(setLoading, setData, 1, PAGE_SIZE, q, setAccessDenied);

        getViaAuth<WorldCupTeam[]>(API.worldCupTeams.getAll).then((res: any) => {
            if (responseValidator(res.status) && res.value) {
                const map: Record<number, WorldCupTeam> = {};
                (res.value as WorldCupTeam[]).forEach((t) => { map[t.id] = t; });
                setTeamsMap(map);
            }
        });
    }, []);

    // ── Debounced keyword search ──────────────────────────────────────────
    const isFirstRender = React.useRef(true);
    React.useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        const timer = setTimeout(() => {
            const normalized = faNumToEnNum(keyword).trim();
            const q = buildQuery(normalized, sortByRef.current, sortDirRef.current);
            setQuery(q);
            setAccessDenied(false);
            getTeamSelectionUsers(setLoading, setData, 1, PAGE_SIZE, q, setAccessDenied);
        }, 500);
        return () => clearTimeout(timer);
    }, [keyword]);

    // ── Sort handlers (immediate, no debounce) ────────────────────────────
    const handleSortByChange = (value: number) => {
        sortByRef.current = value;
        setSortBy(value);
        const normalized = faNumToEnNum(keyword).trim();
        const q = buildQuery(normalized, value, sortDirRef.current);
        setQuery(q);
        setAccessDenied(false);
        getTeamSelectionUsers(setLoading, setData, 1, PAGE_SIZE, q, setAccessDenied);
    };

    const handleSortDirChange = (value: number) => {
        sortDirRef.current = value;
        setSortDirection(value);
        const normalized = faNumToEnNum(keyword).trim();
        const q = buildQuery(normalized, sortByRef.current, value);
        setQuery(q);
        setAccessDenied(false);
        getTeamSelectionUsers(setLoading, setData, 1, PAGE_SIZE, q, setAccessDenied);
    };

    // ── Flag renderer ─────────────────────────────────────────────────────
    const renderTeamCell = (team: SelectedTeam | undefined) => {
        if (!team) return <span className="no-team">—</span>;
        const teamData = teamsMap[team.teamId];
        const flag = teamData?.flag;
        const isUrl = flag && (flag.startsWith('http') || flag.startsWith('/'));
        return (
            <div className="team-cell">
                {isUrl ? (
                    <img src={SERVER_URL + flag} alt={team.teamName} className="flag-sm" />
                ) : flag ? (
                    <span className="flag-emoji-sm">{flag}</span>
                ) : null}
                <span className="team-name-sm">{team.teamName}</span>
            </div>
        );
    };

    // ── Sort indicator helper ─────────────────────────────────────────────
    const sortArrow = sortDirection === 1 ? ' ↑' : ' ↓';

    // ── Columns ───────────────────────────────────────────────────────────
    const columns: TableColumn<TeamSelectionUser>[] = [
        {
            name: 'نام و نام‌خانوادگی',
            id: 'fullName',
            selector: (row) => row.fullName || '-',
            width: '160px',
            cell: (row) => (
                <UserLink userId={row.userId}>
                    <span>{row.fullName || <span className="empty-val">—</span>}</span>
                </UserLink>
            ),
        },
        {
            name: 'شماره موبایل',
            id: 'phoneNumber',
            selector: (row) => row.phoneNumber,
            width: '140px',
            cell: (row) => (
                <CopyablePhone phoneNumber={row.phoneNumber} />
            ),
        },
        {
            name: 'تعداد انتخاب',
            id: 'selectedTeamsCount',
            selector: (row) => row.selectedTeamsCount,
            width: '110px',
            cell: (row) => (
                <Badge
                    text={row.selectedTeamsCount}
                    type={row.selectedTeamsCount >= 3 ? 'success' : 'waiting'}
                />
            ),
        },
        {
            name: sortBy === 2 ? `امتیاز${sortArrow}` : 'امتیاز',
            id: 'score',
            selector: (row) => row.score,
            width: '100px',
            cell: (row) => (
                <span className="score-cell">
                    <StarFilled className="score-star" />
                    {row.score ?? '—'}
                </span>
            ),
        },
        {
            name: sortBy === 1 ? `آخرین تاریخ انتخاب${sortArrow}` : 'آخرین تاریخ انتخاب',
            id: 'lastSelectedDate',
            selector: (row) => row.lastSelectedDate,
            width: '175px',
            cell: (row) => (
                <span className="date-cell">
                    {row.lastSelectedDate
                        ? moment.utc(row.lastSelectedDate).locale('fa').local().format('YYYY/MM/DD HH:mm')
                        : '—'}
                </span>
            ),
        },
        {
            name: 'تیم اول',
            id: 'team1',
            width: '150px',
            cell: (row) => renderTeamCell(row.teams?.find((t) => t.selectionType.id === 1)),
        },
        {
            name: 'تیم دوم',
            id: 'team2',
            width: '150px',
            cell: (row) => renderTeamCell(row.teams?.find((t) => t.selectionType.id === 2)),
        },
        {
            name: 'تیم سوم',
            id: 'team3',
            width: '150px',
            cell: (row) => renderTeamCell(row.teams?.find((t) => t.selectionType.id === 3)),
        },
        {
            name: 'تاریخچه امتیازها',
            id: 'scoreLogs',
            width: '120px',
            center: true,
            cell: (row) => (
                <Tooltip title="مشاهده تاریخچه امتیازها">
                    <button
                        type="button"
                        className="score-logs-btn"
                        onClick={() => openScoreLogs(row)}
                        aria-label="مشاهده تاریخچه امتیازها"
                    >
                        <FileSearchOutlined />
                    </button>
                </Tooltip>
            ),
        },
    ];

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <Page className="pmx-world-cup-participants" title="شرکت‌کننده‌های جام جهانی">
            <Card title="شرکت‌کننده‌های جام جهانی" blur={loading}>
                {accessDenied ? (
                    <Alert
                        className="access-denied-alert"
                        message="دسترسی محدود"
                        description='شما مجوز «لیست کاربرانِ انتخاب‌کننده‌ی تیم» را ندارید. لطفاً با مدیر سیستم تماس بگیرید.'
                        type="error"
                        showIcon
                    />
                ) : (
                    <>
                        <div className="filter-row">
                            <Input
                                value={keyword}
                                className="keyword-search"
                                placeholder="جستجو با نام، نام‌خانوادگی یا شماره موبایل..."
                                name="keyword"
                                prefix={<SearchOutlined />}
                                onChange={(e) => setKeyword(e.target.value)}
                            />
                            <div className="sort-controls">
                                <span className="sort-label">مرتب‌سازی:</span>
                                <Select
                                    value={sortBy}
                                    onChange={handleSortByChange}
                                    options={SORT_BY_OPTIONS}
                                    className="sort-select"
                                    disabled={loading}
                                />
                                <Select
                                    value={sortDirection}
                                    onChange={handleSortDirChange}
                                    options={SORT_DIR_OPTIONS}
                                    className="sort-dir-select"
                                    disabled={loading}
                                />
                            </div>
                            <Button
                                text="خروجی اکسل"
                                type={buttonType.info}
                                icon={<ExportOutlined />}
                                disabled={!data?.items?.length}
                                onClick={() =>
                                    data?.items && downloadCSV('world-cup-participants', flattenForExport(data.items))
                                }
                            />
                        </div>

                        <Table
                            blur={loading}
                            className="participants-table"
                            data={data?.items ?? []}
                            column={columns}
                            pagination={data?.pagination}
                            getdata={getTeamSelectionUsers}
                            setloader={setLoading}
                            setdata={setData}
                            filters={query}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                        />
                    </>
                )}
            </Card>
        </Page>
    );
};
